import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Form, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Languages.css';

const Languages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({
    code: '',
    name: '',
    locale: '',
    direction: 'ltr',
    active: true,
    default: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/api/languages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLanguages(data.data || data);
      } else {
        toast.error('Erreur lors du chargement des langues');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (language = null) => {
    if (language) {
      setEditMode(true);
      setCurrentLanguage(language);
    } else {
      setEditMode(false);
      setCurrentLanguage({
        code: '',
        name: '',
        locale: '',
        direction: 'ltr',
        active: true,
        default: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentLanguage({
      code: '',
      name: '',
      locale: '',
      direction: 'ltr',
      active: true,
      default: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentLanguage(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editMode 
        ? `${baseUrl}/api/languages/${currentLanguage.id}`
        : `${baseUrl}/api/languages`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currentLanguage)
      });

      if (response.ok) {
        toast.success(editMode ? 'Langue modifiée avec succès' : 'Langue ajoutée avec succès');
        fetchLanguages();
        handleCloseModal();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette langue ?',
      text: 'Cette action est irréversible !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#145650',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${baseUrl}/api/languages/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          toast.success('Langue supprimée avec succès');
          fetchLanguages();
        } else {
          toast.error('Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Erreur:', error);
        toast.error('Erreur de connexion au serveur');
      }
    }
  };

  const handleToggleActive = async (language) => {
    try {
      const response = await fetch(`${baseUrl}/api/languages/${language.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...language, active: !language.active })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchLanguages();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleToggleDefault = async (language) => {
    try {
      const response = await fetch(`${baseUrl}/api/languages/${language.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...language, default: !language.default })
      });

      if (response.ok) {
        toast.success('Langue par défaut mise à jour');
        fetchLanguages();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedLanguages = [...languages].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredLanguages = sortedLanguages.filter(lang =>
    lang.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.locale?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-languages-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h2 className="page-title">
              <i className="fa fa-language me-3"></i>
              Langues
            </h2>
            <p className="page-subtitle">
              Affichage des éléments 1 à {filteredLanguages.length} sur {languages.length}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons mb-4">
        <Button className="btn-add" onClick={() => handleShowModal()}>
          <i className="fa fa-plus me-2"></i>
          Ajouter langue
        </Button>
        <Button className="btn-reorder" variant="secondary">
          <i className="fa fa-sort me-2"></i>
          Réordonner langues
        </Button>
        <Button className="btn-sync" variant="success">
          <i className="fa fa-sync me-2"></i>
          Sync. Languages Files Lines
        </Button>
        <Button className="btn-translate" variant="primary">
          <i className="fa fa-globe me-2"></i>
          Translate site texts
        </Button>
      </div>

      {/* Search Bar */}
      <Card className="search-card mb-4">
        <Card.Body>
          <Form.Control
            type="text"
            placeholder="Rechercher par nom, code ou locale..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </Card.Body>
      </Card>

      {/* Languages Table */}
      <Card className="table-card">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
              <p className="mt-3">Chargement...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="languages-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                      <i className="fa fa-filter me-2"></i>
                      Code
                      {sortConfig.key === 'code' && (
                        <i className={`fa fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-2`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                      <i className="fa fa-filter me-2"></i>
                      Nom
                      {sortConfig.key === 'name' && (
                        <i className={`fa fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-2`}></i>
                      )}
                    </th>
                    <th>Locale</th>
                    <th>Direction</th>
                    <th>Active</th>
                    <th>Default</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLanguages.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                        <p>Aucune langue trouvée</p>
                      </td>
                    </tr>
                  ) : (
                    filteredLanguages.map((language) => (
                      <tr key={language.id}>
                        <td><strong>{language.code}</strong></td>
                        <td>
                          <a href="#" className="language-name">{language.name}</a>
                        </td>
                        <td>{language.locale}</td>
                        <td>{language.direction}</td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={language.active}
                            onChange={() => handleToggleActive(language)}
                            className="custom-switch"
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={language.default}
                            onChange={() => handleToggleDefault(language)}
                            className="custom-switch"
                          />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            className="btn-edit me-2"
                            onClick={() => handleShowModal(language)}
                          >
                            <i className="fa fa-edit"></i> Modifier
                          </Button>
                          <Button
                            size="sm"
                            className="btn-delete"
                            onClick={() => handleDelete(language.id)}
                          >
                            <i className="fa fa-trash"></i> Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Modifier la langue' : 'Ajouter une langue'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Code *</Form.Label>
              <Form.Control
                type="text"
                name="code"
                value={currentLanguage.code}
                onChange={handleInputChange}
                placeholder="fr, en, es..."
                required
                maxLength={2}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nom *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={currentLanguage.name}
                onChange={handleInputChange}
                placeholder="French, English, Spanish..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Locale *</Form.Label>
              <Form.Control
                type="text"
                name="locale"
                value={currentLanguage.locale}
                onChange={handleInputChange}
                placeholder="fr_FR, en_US, es_ES..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Direction</Form.Label>
              <Form.Select
                name="direction"
                value={currentLanguage.direction}
                onChange={handleInputChange}
              >
                <option value="ltr">LTR (Left to Right)</option>
                <option value="rtl">RTL (Right to Left)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="active"
                label="Active"
                checked={currentLanguage.active}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="default"
                label="Langue par défaut"
                checked={currentLanguage.default}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Annuler
              </Button>
              <Button type="submit" className="btn-save">
                {editMode ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default Languages;
